/**
 * Product image hover zoom (desktop / mouse only).
 * - Moving the mouse over a product image zooms in where the pointer is.
 * - Clicking the image does NOT open the lightbox on desktop (the expand button does).
 * - On touch devices nothing changes: tapping the image opens the lightbox.
 */
(() => {
  if (window.__productHoverZoom) return;
  window.__productHoverZoom = true;

  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const SELECTOR = 'media-gallery[data-hover-zoom] .product-media-container--image';

  /** @type {HTMLElement | null} */
  let active = null;

  /** @param {EventTarget | null} target */
  const getContainer = (target) => {
    if (!(target instanceof Element)) return null;
    const container = target.closest(SELECTOR);
    if (!container || container.closest('dialog')) return null;
    return /** @type {HTMLElement} */ (container);
  };

  /** @param {HTMLElement} container */
  const getImage = (container) => container.querySelector('img.product-media__image');

  /** @param {HTMLElement} container */
  const activate = (container) => {
    const gallery = container.closest('media-gallery');
    const scale = parseFloat(gallery?.getAttribute('data-hover-zoom-scale') || '') || 2.2;
    container.style.setProperty('--hover-zoom-scale', String(scale));
    container.classList.add('is-hover-zoom');

    // Ask the browser for a sharper image the first time this one is zoomed.
    const img = getImage(container);
    if (img instanceof HTMLImageElement && !img.dataset.hoverZoomHiRes) {
      img.dataset.hoverZoomHiRes = 'true';
      const width = Math.ceil(img.getBoundingClientRect().width * scale);
      if (width > 0 && img.srcset) img.sizes = `${Math.min(width, 3840)}px`;
    }
  };

  /** @param {HTMLElement | null} container */
  const deactivate = (container) => {
    if (!container) return;
    container.classList.remove('is-hover-zoom');
    const img = getImage(container);
    if (img instanceof HTMLElement) img.style.removeProperty('transform-origin');
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse' || !fineHover.matches) return;

      const container = getContainer(event.target);
      const overButton = event.target instanceof Element && event.target.closest('.product-media__expand');

      if (container !== active || overButton) {
        deactivate(active);
        active = overButton ? null : container;
        if (active) activate(active);
      }
      if (!active) return;

      const media = active.querySelector('.product-media') || active;
      const rect = media.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
      const img = getImage(active);
      if (img instanceof HTMLElement) img.style.transformOrigin = `${x}% ${y}%`;
    },
    { passive: true }
  );

  document.addEventListener('pointerout', (event) => {
    if (!active) return;
    const next = event.relatedTarget;
    if (next instanceof Node && active.contains(next)) return;
    deactivate(active);
    active = null;
  });

  window.addEventListener('blur', () => {
    deactivate(active);
    active = null;
  });

  // Block the "click image → open lightbox" behaviour for mouse users.
  // Runs on window (capture) so it fires before the theme's delegated handlers.
  window.addEventListener(
    'click',
    (event) => {
      if (!fineHover.matches) return;
      if (event.target instanceof Element && event.target.closest('.product-media__expand')) return;
      if (!getContainer(event.target)) return;
      // Leave real touch taps on hybrid devices alone.
      if (/** @type {PointerEvent} */ (event).pointerType && /** @type {PointerEvent} */ (event).pointerType !== 'mouse') return;

      event.stopPropagation();
      event.preventDefault();
    },
    true
  );
})();
